import {startTransition, useCallback, useEffect, useRef, useState} from 'react';
import {useDataFetcherAction, useDataFetcherInitialData} from './context';
import {
  DEFAULT_QUERY_CONFIG,
  UseQueryOptions,
  UseQueryResultWithUpdateCache,
  refetchServer,
  updateCacheServer,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from './react-query';
import {throwValidatedDecorator} from './throwable';
import {SuccessResult} from './types';

import {doAsync, exists} from '@aboutyou/utils';
import {useServerAwaitMetadata} from './ServerAwaitMetadataProvider';
import {useRequestNonCriticalErrorMessagePatterns} from './grpc/GrpcClientContext';
import {logUseRequestError} from './logUseRequestError';
import {hasUnresolvedStream, isEmptyStream} from './stream/utils';
import {useQueryWithStaleFallbackData} from './useQueryWithStaleFallbackData';

export interface Config<T> {
  /**
   * called once right after the component is rendered and the initial state is fetched from the server
   */
  hydrate?: (data: SuccessResult<T>) => T;

  staleTime?: UseQueryOptions<T>['staleTime'];
  cacheTime?: UseQueryOptions<T>['gcTime'];
  refetchInterval?: UseQueryOptions<T>['refetchInterval'];
  refetchOnMount?: UseQueryOptions<T>['refetchOnMount'];
  retry?: UseQueryOptions<T>['retry'];
  /**
   * Allows to keep the state that was available before a new loading is triggered to e.g. not annoy the user with loading spinner and flickering. It can take 3 different states. `true` (default) it will keep the state as long as the new data is loaded. `false` it will switch immediate to show the loading state when the data fetching is triggered. `keep-until-frustrating` will keep the state for 500ms active and switches if the request takes too long.
   */
  keepPreviousData?: boolean | 'keep-until-frustrating';

  /**
   * Setting this to true will keep the previous data when an error occurs in a refetch on the client.
   * This will not keep the previous data if the keepPreviousData is set to false.
   */
  keepPreviousDataOnClientError?: boolean;

  onSuccess?: (data: T) => void;

  /**
   * Fire-and-forget side effect (e.g. tracking, toasts) that runs whenever this
   * observer receives resolved data — on a fetch AND on a cache hit — without
   * forcing a refetch. Client-only: it never runs during SSR. No memoization
   * needed; it fires once per distinct data instance, so `keepPreviousData`
   * placeholders are skipped. It fires deferred, off the render path, and a
   * throw is logged and contained. Anything that must throw belongs in
   * `validateData`.
   */
  onData?: (data: T) => void;

  onInitialDataSuccess?: (data: T) => void;

  onError?: (error: unknown) => void;

  disabled?: boolean;

  /**
   * if `nonEssential` is set to `true` the error for this request will not be considered as a critical error and will not be logged to Monitoring
   */
  nonEssential?: boolean;

  validateData?: (data: T) => void;
  validateError?: (error: unknown) => void;

  /**
   * Every time this token changes a refetch will be triggered automatically.
   * In combination with `keepPreviousData` this e.g. allow to avoid loading states and
   * update in the background even after the user leaving the current page but coming back later.
   */
  refetchToken?: string[] | string;
}

// eslint-disable-next-line complexity
export const useRequest = <T extends object>(
  queryKey: string | string[],
  fetcherFunction: () => Promise<T> | T,
  config?: Config<T>,
): DataFetcherRequest<T & {validated?: Record<string, unknown> | null}> => {
  const previousStreamData = useRef<T[] | undefined>(undefined);

  const cacheKey = generateCacheKey(queryKey);
  const result = useDataFetcherInitialData<T>(cacheKey);
  const nonCriticalErrorMessagePatterns =
    useRequestNonCriticalErrorMessagePatterns();
  const {deleteKey} = useDataFetcherAction();
  const {setMetadataHandled, waitUntilMetadataHandled} =
    useServerAwaitMetadata();

  if (!process.browser) {
    if (config?.disabled) {
      return {
        ...result,
        isLoading: false,
        isStale: false,
        isIdle: true,
        isPlaceholderData: false,
        isFetching: false,
        isFetched: false,
        isFetchedAfterMount: false,
        updateCache: updateCacheServer,
        refetch: refetchServer,
      };
    }

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const query = useSuspenseQuery<T>(
      cacheKey,
      throwValidatedDecorator({
        fetcherFunction,
        config,
        nonCriticalErrorMessagePatterns,
        cacheKey,
        setMetadataHandled,
        waitUntilMetadataHandled,
      }),
    );

    return {
      ...query,
      updateCache: updateCacheServer,
      refetch: refetchServer,
    } as unknown as DataFetcherRequest<T>;
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const queryClient = useQueryClient();
  const hasInitialData = exists(result) && 'data' in result;

  // latest-ref pattern: the select callback below stays referentially stable while
  // always calling the freshest `onData` closure
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const onDataRef = useRef(config?.onData);
  onDataRef.current = config?.onData;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const lastOnDataSeen = useRef<T | undefined>(undefined);
  // `select` may re-run with data this observer already presented — e.g. a
  // `keepPreviousData` placeholder reuses the previous query's data object on a key
  // change — so dedupe by data reference to fire `onData` once per distinct instance
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const onDataSelect = useCallback(
    (data: T): T => {
      if (lastOnDataSeen.current !== data) {
        lastOnDataSeen.current = data;
        // `select` runs during render, so the side effect is deferred off the
        // render path (mirroring the custom `onSuccess` in react-query.ts) and
        // contained: a throwing `onData` must not put the query into error state
        doAsync(() => {
          startTransition(() => {
            try {
              onDataRef.current?.(data);
            } catch (error) {
              logUseRequestError({cacheKey, error});
            }
          });
        });
      }

      return data;
    },
    [cacheKey],
  );

  const {hydrate, ...queryConfig} = {
    hydrate(result: SuccessResult<T>) {
      return result.data;
    },
    enabled: !config?.disabled,
    ...DEFAULT_QUERY_CONFIG,
    ...config,
    // adapt the keepPreviousData to the new react-query API
    ...(config?.keepPreviousData === false
      ? {
          keepPreviousData: false,
          placeholderData: undefined,
        }
      : {
          keepPreviousData:
            config?.keepPreviousData === 'keep-until-frustrating'
              ? ('keep-until-frustrating' as const)
              : true, // defaults to true
          placeholderData: DEFAULT_QUERY_CONFIG.placeholderData,
        }),
    // adapt the cacheTime to the new react-query API
    ...(config?.cacheTime !== undefined
      ? {gcTime: config.cacheTime}
      : {gcTime: DEFAULT_QUERY_CONFIG.gcTime}),
  };

  // if a component is rendered multiple times in the same session the state might have already mutated. We should therefore delete it after hydration to not get outdated data for some reason
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => deleteKey(cacheKey), [deleteKey, cacheKey]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (hasInitialData) {
      // call onInitialDataSuccess once when the server data is hydrated
      if (config?.onInitialDataSuccess) {
        config.onInitialDataSuccess(result.data as T);
      }
    }
  }, [config, hasInitialData]);

  const initialData = (() => {
    if (hasInitialData) {
      return hydrate(result as SuccessResult<T>);
    }
    return undefined;
  })();

  // the name might eventually collapse with the one in useQuery API
  const pureQueryConfig = {...queryConfig};
  delete pureQueryConfig.validateData;
  delete pureQueryConfig.validateError;
  delete pureQueryConfig.disabled;
  // `onData` is mapped onto `select` below; it is not a React Query option itself.
  delete pureQueryConfig.onData;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const originalQuery = useQuery<T, Error>(
    cacheKey,
    () =>
      throwValidatedDecorator({
        fetcherFunction,
        config,
        nonCriticalErrorMessagePatterns,
        cacheKey,
      })().then(data => {
        subscribeToStream(data);
        return data;
      }),
    {
      initialData,
      ...(pureQueryConfig as Omit<UseQueryOptions<T, Error>, 'queryKey'>),
      ...(config?.onData ? {select: onDataSelect} : {}),
    },
  );

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const query = useQueryWithStaleFallbackData<T>({
    request: originalQuery,
    cacheKey,
    queryConfig,
  });

  const queryResult = {
    ...query,
    isIdle: query.fetchStatus === 'idle',
  } as DataFetcherRequest<T & {validated?: Record<string, unknown> | null}>;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const forceLoadingState = useKeepUntilFrustrating<T>({
    cacheKey,
    queryResult,
    shouldKeepPreviousUntilFrustrating:
      queryConfig.keepPreviousData === 'keep-until-frustrating',
    disabled: queryConfig.disabled,
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    const lastTokenKey = `${cacheKey}-tokenKey`;
    if (config?.refetchToken !== undefined && !config?.disabled) {
      const lastToken = queryClient.getQueryData(lastTokenKey);
      if (lastToken !== config.refetchToken) {
        if (lastToken !== undefined) {
          queryResult.refetch();
        }
        queryClient.setQueryDefaults(lastTokenKey, {
          gcTime: Infinity,
        });
        queryClient.setQueryData(lastTokenKey, config.refetchToken);
      }
    }
  }, [config?.refetchToken, cacheKey, queryClient, config?.disabled]);

  // Only the query function may call this: every subscribe sends its own request.
  function subscribeToStream(response: T) {
    if (hasUnresolvedStream(response)) {
      response.stream.subscribe({
        next: data => {
          const streamItems = queryClient.setQueryData<{
            items: T[];
            trailers: Record<string, string[]>;
          }>(cacheKey, currentQueryData => {
            const items = currentQueryData?.items.concat(data) ?? [data];
            return {
              items,
              trailers: currentQueryData?.trailers ?? {},
              subscribed: true,
            };
          });

          // stream.items does not exist on origin observable, we mutate it here just for the GRPC plugin
          // @ts-expect-error -- IGNORE: mutating the data object to add stream.items for grpc plugin
          response.stream.items = streamItems?.items;
        },
        error: async (error: Error) => {
          logUseRequestError({cacheKey, error});

          queryClient.setQueryData<{
            streamError: Error;
          }>(cacheKey, () => {
            return {streamError: error, subscribed: true};
          });
        },
        complete: () => {
          // if the stream has ended and was empty
          // update query data not to be undefined
          // this will internally make query successful and set isLoading to false
          if (isEmptyStream(queryClient.getQueryData(cacheKey))) {
            previousStreamData.current = undefined;

            queryClient.setQueryData<{items: T[]}>(cacheKey, () => ({
              items: [],
              subscribed: true,
            }));
          }
        },
      });
    }
  }

  // Preserve the items object from the stream request if keepPreviousData is true
  if (
    config?.keepPreviousData &&
    queryResult.data &&
    'items' in queryResult.data &&
    (queryResult.data.items as T[]).length > 0
  ) {
    previousStreamData.current = queryResult.data.items as T[];
  }

  if (queryResult.data && 'streamError' in queryResult.data) {
    // in case error is undefined
    let error = new Error('Unknown error happened');

    if (queryResult.data.streamError instanceof Error) {
      error = queryResult.data.streamError;
    }

    return {
      ...queryResult,
      data: undefined,
      isLoading: false,
      error,
    };
  }

  if (hasUnresolvedStream(queryResult.data)) {
    if (queryResult.data.subscribed === false) {
      return {
        ...queryResult,
        data: undefined,
        isLoading: true,
      };
    }

    if (config?.keepPreviousData && previousStreamData.current) {
      return {
        ...queryResult,
        data: {
          ...queryResult.data,
          items: previousStreamData.current,
        },
        isLoading: false,
      };
    }

    return {
      ...queryResult,
      data: undefined,
      isLoading: true,
    };
  }

  if (forceLoadingState) {
    return {
      ...queryResult,
      data: undefined,
      isLoading: true,
    };
  }

  return queryResult;
};

export const generateCacheKey = (queryKey: string | string[]) => {
  return JSON.stringify(queryKey);
};

export interface DataFetcherRequest<
  Response extends object,
  Error = unknown,
  UpdateData = Response,
> {
  data?: Response & {subscribed?: boolean};
  error?: Error;
  isLoading: boolean;
  isStale: boolean;
  isIdle: boolean;
  isFetching: boolean;
  isFetched: boolean;
  isPlaceholderData: boolean;
  isFetchedAfterMount: boolean;
  refetch: UseQueryResultWithUpdateCache<Response, Error>['refetch'];
  updateCache: (data: UpdateData | undefined) => void;
}

const FRUSTRATING_TIMEOUT = 500;
const useKeepUntilFrustrating = <TData extends object>({
  cacheKey,
  queryResult,
  shouldKeepPreviousUntilFrustrating,
  disabled,
}: {
  cacheKey: string;
  queryResult: DataFetcherRequest<TData, unknown>;
  shouldKeepPreviousUntilFrustrating: boolean;
  disabled?: boolean;
}) => {
  const [forceLoadingState, setForceLoadingState] = useState(false);

  useEffect(() => {
    if (!shouldKeepPreviousUntilFrustrating) {
      return;
    }
    const id = setTimeout(() => {
      if (queryResult.isPlaceholderData && !disabled) {
        // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
        setForceLoadingState(true);
      }
    }, FRUSTRATING_TIMEOUT);
    return () => {
      clearTimeout(id);
      setForceLoadingState(false);
    };
  }, [
    cacheKey,
    queryResult.isPlaceholderData,
    shouldKeepPreviousUntilFrustrating,
    disabled,
  ]);

  return forceLoadingState;
};
